import { useState } from "react";

import { ApiError } from "@/api/client";
import type { ProjectSummary } from "@/api/hooks";
import {
  classifyProjectArchiveFailure,
  useArchiveProjectMutation,
  useRestoreProjectMutation,
} from "@/api/projectArchive";
import { projectArchiveRefusalKnownCopy } from "@/lib/projectArchive";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";

export type ProjectLifecycleMode = "archive" | "restore";

/**
 * The archive/restore confirmation for a Project (#440, over #434).
 *
 * `LifecycleDialog`'s shape without its impact query: a Project archive has
 * no adoption blast radius to show — it hides ONE project from the active
 * list and changes nothing else — so the confirmation's job is the retention
 * statement itself, and the confirm control is live as soon as the dialog
 * has a revision to send.
 *
 * COMPARE-AND-SET, the retarget's discipline: the revision sent is the one
 * this dialog was opened with, snapshotted at submit — the prop may already
 * hold a newer value by the time a refusal renders, and the alert must name
 * the revision the decision was actually made against. A stale refusal here
 * has one extra property the retarget's does not: the contract says "the
 * state you wanted may already hold", so the mutation hook re-reads the list
 * and this alert says to check rather than re-offering the action.
 *
 * There is deliberately no delete flow here or anywhere else — archive is
 * the only lifecycle exit, and every candidate, publication, evidence record
 * and pin survives it byte-for-byte (#434's preservation proof).
 */
export function ProjectLifecycleDialog({
  project,
  mode,
  onClose,
}: {
  project: Pick<ProjectSummary, "id" | "name" | "archive">;
  mode: ProjectLifecycleMode;
  onClose: () => void;
}) {
  const archive = useArchiveProjectMutation(project.id);
  const restore = useRestoreProjectMutation(project.id);
  const mutation = mode === "archive" ? archive : restore;
  const busy = mutation.isPending;
  const [sentRevision, setSentRevision] = useState<number | null>(null);

  // The generated field is optional (an older service could omit it). A write
  // must send only what was read — never a defaulted 0 — so an absent status
  // fails the confirmation closed with its reason instead of guessing.
  const revision = project.archive?.archiveRevision;

  const failure = mutation.isError
    ? classifyProjectArchiveFailure(mutation.error)
    : null;
  // Both outcomes require a new decision against the refreshed Project. The
  // old CAS must not remain an enabled retry: stale is known-old, while an
  // interrupted answer may have committed and advanced it.
  const mustCheckRefreshedProject =
    failure?.kind === "stale" || failure?.kind === "ambiguous";

  async function confirm() {
    if (revision === undefined || mustCheckRefreshedProject) {
      return;
    }
    setSentRevision(revision);
    try {
      await mutation.mutateAsync({ expectedArchiveRevision: revision });
      onClose();
    } catch {
      // Rendered below from mutation.error; the dialog stays open so the
      // refusal is read beside the decision that provoked it.
    }
  }

  const serverMessage =
    mutation.error instanceof ApiError ? mutation.error.message : null;

  // The copy ladder per kind. `refused` keeps LifecycleDialog's middle rung —
  // our copy for a reason we know, else THE SERVER'S OWN SENTENCE, else
  // generic — because the day the reason enum grows a member this build has
  // no copy for, the server is still holding a sentence that describes it.
  let alert: string | null = null;
  let mono: string | null = null;
  if (failure) {
    switch (failure.kind) {
      case "stale":
        alert =
          `This change was decided against archive revision ${sentRevision ?? "unknown"}. ` +
          (failure.currentRevision === undefined
            ? "The Editor Service now reports a newer revision. "
            : `The Editor Service now reports revision ${failure.currentRevision}. `) +
          "The state you wanted may already hold — close this and re-read the project before deciding again.";
        break;
      case "refused":
        alert =
          projectArchiveRefusalKnownCopy(failure.reason) ??
          serverMessage ??
          "The server will not change this project's archive state.";
        break;
      case "denied":
        alert = "Your account is not authorized to change this project's archive state.";
        break;
      case "unavailable":
        alert = "This project lifecycle action is not available in this environment.";
        break;
      case "ambiguous":
        alert =
          "The Editor Service's answer was interrupted, so the result is unknown. " +
          "The change may already have succeeded — close this and re-read the project before deciding again.";
        mono = `${failure.code}${failure.requestId ? ` · ${failure.requestId}` : ""}`;
        break;
      case "error":
        alert = "The Editor Service did not accept this change. Nothing was changed.";
        mono = `${failure.code}${failure.requestId ? ` · ${failure.requestId}` : ""}`;
        break;
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={
          mode === "archive"
            ? `Archive "${project.name}"?`
            : `Restore "${project.name}"?`
        }
        description={
          mode === "archive"
            ? "The project leaves the active list. Every candidate, publication, evidence record and pin is retained exactly as it is — archiving is not deletion, and nothing about any deployment changes. Restoring it later is an administrator action."
            : "The project returns to the active list with everything exactly as it was retained. Restore is an audited administrator action."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirm()}
              disabled={
                busy || revision === undefined || mustCheckRefreshedProject
              }
            >
              {mode === "archive" ? "Archive project" : "Restore project"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {revision === undefined ? (
            <p className="text-sm text-muted-foreground">
              The Editor Service did not report this project&apos;s archive
              revision, so the change cannot be sent. Reload and try again.
            </p>
          ) : null}
          {alert ? (
            <p role="alert" className="text-sm text-destructive">
              {alert}
            </p>
          ) : null}
          {mono ? (
            <p className="font-mono text-xs text-muted-foreground">{mono}</p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
